import { Component } from "@angular/core";
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterOutlet,
} from "@angular/router";
import { TranslocoDirective } from "@jsverse/transloco";
import * as Sentry from "@sentry/angular";
import { ClerkService } from "ngx-clerk-iliad";
import { ProgressBarModule } from "primeng/progressbar";
import { ToastModule } from "primeng/toast";
import { environment } from "../environments/environment";
import { ClerkOptionsService } from "./services/clerk-options.service";
import { LocalizationService } from "./services/localization.service";

@Component({
  selector: "app-root",
  imports: [RouterOutlet, TranslocoDirective, ToastModule, ProgressBarModule],
  template: `
    <!-- Wrap entire application in Transloco directive, so translations get loaded ASAP -->
    <ng-container *transloco="let t">
      @if (navigationInProgress) {
        <p-progressbar mode="indeterminate" />
      }
      <p-toast />
      <router-outlet />
    </ng-container>
  `,
  styles: `
    p-progressbar {
      position: fixed;
      width: 100%;
      --p-progressbar-height: 4px;
    }
  `,
})
export class AppComponent {
  public navigationInProgress = false;

  constructor(
    private _clerk: ClerkService,
    private _clerkOptions: ClerkOptionsService,
    private _localizationService: LocalizationService,
    private _router: Router,
  ) {
    const satelliteOptions = this._clerkOptions.getSatelliteOptions(environment);
    this._clerk.__init({
      publishableKey: environment.clerk.publishableKey,
      clerkJSVersion: "5.87.0",
      localization: this._localizationService.getClerkLocalization(),
      telemetry: false,
      signInUrl: satelliteOptions.signInUrl,
      signUpUrl: satelliteOptions.signUpUrl,
      isSatellite: satelliteOptions.isSatellite,
      domain: satelliteOptions.domain,
      allowedRedirectOrigins: satelliteOptions.allowedRedirectOrigins,
    });

    this._router.events.subscribe((event) => {
      switch (true) {
        case event instanceof NavigationStart: {
          this.navigationInProgress = true;
          break;
        }

        case event instanceof NavigationError: {
          // Handle error when a lazy-loaded module fails to load, usually when a new version is deployed and the user has an old version open
          // This happens during navigation so it is somewhat ok to reload the page (most users won't notice)

          if (event.error?.message?.includes("dynamically imported module")) {
            // Firefox/Chrome: "Failed to load (or fetch) dynamically imported module"
            location.reload();
          } else if (event.error?.message?.includes("module script failed")) {
            // Safari: "Importing a module script failed" (according to online forums)
            location.reload();
          } else {
            // Log other errors to Sentry for further investigation
            // Add some proper toasts here if we see instances of these errors actually happening
            Sentry.captureException(event.error);
          }

          break;
        }

        case event instanceof NavigationEnd:
        case event instanceof NavigationCancel: {
          this.navigationInProgress = false;
          break;
        }

        default: {
          break;
        }
      }
    });
  }

  title = "Connect";
}
